<template>
    <div class="ai-handoff">
        <div v-if="!open" class="ai-btn ai-btn-ghost ai-handoff-open" @click="show()">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
            Send to someone else to build
        </div>
        <div v-else class="ai-handoff-panel">
            <div class="ai-handoff-row">
                <label class="ai-handoff-label" for="ai-handoff-to">Who builds it?</label>
                <select id="ai-handoff-to" v-model="toUserID" :disabled="busy || loading" class="ai-handoff-select">
                    <option :value="null">{{ loading ? 'Loading…' : builders.length ? 'Choose a person…' : 'No one else can edit the playbook' }}</option>
                    <option v-for="b in builders" :key="b.userID" :value="b.userID">{{ b.name }}</option>
                </select>
                <div class="ai-btn" :class="{ 'disable-button': !toUserID || busy }" @click="send()">
                    {{ busy ? (pct !== null ? 'Sending… ' + pct + '%' : 'Sending…') : 'Send the recording' }}
                </div>
                <a v-if="!busy" class="ai-handoff-cancel" @click.prevent="open = false">Cancel</a>
            </div>
            <p class="ai-handoff-hint">They get an email with the recording, and it waits under <strong>My AI Captures</strong> for you both until they build or discard it. Your note above goes with it. Nothing is sent to the AI until they press Build. It's set to build a <strong>{{ typeNoun }}</strong>; they can change that, and whether the video is kept, before they build.</p>
            <div v-if="error" class="ai-error">{{ error }}</div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';

// "Record your screen, but don't build the process" (Keelan, 22 Oct 2026): hands a
// Forge screen recording (or an uploaded one) to another editor, who builds it
// later from My AI Captures. Pure hand-off — no AI call happens here.
//   recording: { blob, durationSec, mimeType } from AIScreenRecorder, or
//   file: an uploaded video File; note: the description typed alongside it;
//   nodeType: what the sender wants built ('process' | 'checklist' | 'sheet' | 'timeline').
// Emits sent({ jobId, to }) once the upload is accepted.
const TYPE_NOUNS: { [k: string]: string } = { process: 'process', checklist: 'powerlist', sheet: 'role sheet', timeline: 'value engine' };
export default defineComponent({
    name: 'AIRecordingHandoff',
    props: { recording: Object, file: Object, note: { type: String, default: '' }, nodeType: { type: String, default: 'process' } },
    emits: ['sent'],
    computed: {
        typeNoun(): string { return TYPE_NOUNS[this.nodeType] || 'process'; }
    },
    data() {
        return {
            open: false,
            loading: false,
            loaded: false,
            builders: [] as any[],
            toUserID: null as any,
            busy: false,
            pct: null as any,
            error: ''
        };
    },
    methods: {
        async show() {
            this.open = true;
            this.error = '';
            if (this.loaded || this.loading) return;
            this.loading = true;
            try {
                this.builders = (await axios.get(`${this.$store.state.apiUrl}/api/ai/builders`)).data || [];
                this.loaded = true;
            } catch (e: any) {
                this.error = 'Could not load who can build this — please try again.';
            } finally {
                this.loading = false;
            }
        },
        async send() {
            if (this.busy || !this.toUserID) return;
            if (!this.recording && !this.file) { this.error = 'Record or upload the screen recording first.'; return; }
            this.busy = true;
            this.error = '';
            this.pct = 0;
            const formData = new FormData();
            if (this.recording) {
                const rec: any = this.recording;
                const ext = (rec.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
                formData.append('video', rec.blob, 'screen-recording.' + ext);
                formData.append('durationSec', String(Math.round(rec.durationSec || 0)));
            } else {
                formData.append('video', this.file as any);
            }
            if (this.note && this.note.trim()) formData.append('note', this.note.trim());
            formData.append('toUserID', String(this.toUserID));
            formData.append('nodeType', this.nodeType || 'process');
            try {
                const resp = await axios.post(`${this.$store.state.apiUrl}/api/ai/recordings/handoff`, formData, {
                    timeout: 600000,
                    onUploadProgress: (e: any) => {
                        const pct = e.total ? Math.round((e.loaded / e.total) * 100) : null;
                        this.pct = pct !== null && pct >= 100 ? null : pct;
                    }
                });
                const to = (this.builders.find((b: any) => b.userID == this.toUserID) || {}).name || resp.data.to || 'them';
                this.$emit('sent', { jobId: resp.data.jobId, to });
                this.open = false;
                this.toUserID = null;
            } catch (e: any) {
                this.error = (e && e.response && typeof e.response.data === 'string' && e.response.data.length < 300 && e.response.data) || 'The recording could not be sent — please try again.';
            } finally {
                this.busy = false;
                this.pct = null;
            }
        }
    }
});
</script>

<style scoped>
.ai-handoff { margin-top: 12px; }
.ai-handoff-open { cursor: pointer; }
.ai-handoff-panel { border: 1px solid var(--ai-border-soft, #f0dcc8); background: #fffaf5; border-radius: 12px; padding: 12px 14px; }
.ai-handoff-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ai-handoff-label { font-size: 13px; font-weight: 700; color: #4a3728; }
.ai-handoff-select { flex: 1; min-width: 180px; max-width: 320px; margin: 0 !important; padding: 7px 9px !important; font-size: 13px !important; }
.ai-handoff-cancel { font-size: 13px; cursor: pointer; color: #8a7f75; text-decoration: underline; }
.ai-handoff-hint { margin: 8px 0 0; font-size: 12.5px; line-height: 1.5; color: #6f655c; }
.ai-handoff .ai-error { margin-top: 8px; }
</style>
